export const INCIDENT_POLLING_MS = 6000;

export const APPROVE_ACTION = {
  APPROVE: 'approve',
  NO_APPROVE: 'noApprove',
  REJECT: 'reject',          // CH rechaza con motivo obligatorio
  REVIEW: 'review',          // CH revisa: decide aprobar o rechazar desde el modal
} as const;

export type ApproveActionType = typeof APPROVE_ACTION[keyof typeof APPROVE_ACTION];

// Catálogo legacy (flags binarios); mantenido para compatibilidad con IncidentPayrollTable legacy
export const INCIDENT_STATUS = {
  ENVIADA: 'Enviada',
  APROBADA: 'Aprobada',
  AUTORIZADA: 'Autorizada',
} as const;

export type IncidentStatusType = typeof INCIDENT_STATUS[keyof typeof INCIDENT_STATUS];

// Catálogo HU-PER-04: valores que expone el backend en requestStatus
export const REQUEST_STATUS = {
  ENVIADA: 'Enviada',
  APROBADA_LIDER: 'AprobadaLider',
  AUTORIZADA: 'Autorizada',
  RECHAZADA: 'Rechazada',
  ESCALADA: 'Escalada',
} as const;

export type RequestStatusType = typeof REQUEST_STATUS[keyof typeof REQUEST_STATUS];

interface IncidentStatusFlags {
  ApprovedDirect: number | boolean | string;
  ApprovedRH: number | boolean | string;
  requestStatus?: string;
  approvedByName?: string;
  escalatedToCH?: number | boolean;
}

export function getIncidentStatus(row: IncidentStatusFlags): IncidentStatusType | '' {
  if (!row.ApprovedDirect && !row.ApprovedRH) return INCIDENT_STATUS.ENVIADA;
  if (row.ApprovedDirect && !row.ApprovedRH) return INCIDENT_STATUS.APROBADA;
  if (row.ApprovedDirect && row.ApprovedRH) return INCIDENT_STATUS.AUTORIZADA;
  return '';
}

// Prioriza requestStatus del backend; si no existe, deriva desde los flags (no-destructivo)
export function getRequestStatus(row: IncidentStatusFlags): RequestStatusType | '' {
  if (row.requestStatus) {
    const match = Object.values(REQUEST_STATUS).find(v => v === row.requestStatus);
    if (match) return match as RequestStatusType;
  }
  if (!row.ApprovedDirect && !row.ApprovedRH) return REQUEST_STATUS.ENVIADA;
  if (row.ApprovedDirect && !row.ApprovedRH) return REQUEST_STATUS.APROBADA_LIDER;
  if (row.ApprovedDirect && row.ApprovedRH) return REQUEST_STATUS.AUTORIZADA;
  return '';
}

// DBSB-343 — Comentario a mostrar en la columna "Comentario CH" del listado.
// Sobre una incidencia existen DOS comentarios distintos:
//   · Comentarios     → el capturado al ALTA. En Dashboard (alta directa de CH)
//                       lo escribe CH y es el ÚNICO comentario que existe.
//   · commentsAproved → la nota que CH escribe al APROBAR una solicitud (flujo
//                       con aprobación; no existe en el alta directa).
// La columna prioriza la nota de aprobación; si la incidencia se autorizó sin
// ella (alta directa de CH, que solo trae el comentario de captura) cae a ese
// comentario para que la información de CH no parezca perdida. Se limita a
// incidencias AUTORIZADAS para no exponer el comentario del solicitante en
// solicitudes de líder aún pendientes de autorización.
export function getComentarioCH(
  row: IncidentStatusFlags & { commentsAproved?: string | null; Comentarios?: string | null },
): string {
  if (getRequestStatus(row) !== REQUEST_STATUS.AUTORIZADA) return '';
  return String(row.commentsAproved || row.Comentarios || '');
}

// Detecta filas de vacaciones por el nombre del concepto.
// Prioriza ConceptName ('Vacaciones pagadas') sobre TipoConcepto ('Percepcion') porque
// en los datos reales del SP TipoConcepto siempre vale 'Percepcion'/'Deduccion' y nunca
// contiene el nombre del concepto — el || haría cortocircuito y jamás llegaría a ConceptName.
export function isVacaciones(row: any): boolean {
  const label = String(row?.ConceptName || row?.TipoConcepto || '').toLowerCase();
  return label.includes('vacacion');
}

// MOCK — eliminar cuando el backend exponga requestStatus, approvedByName y saldo de vacaciones
export function mockRequestData(row: any): any {
  const base = {
    ...row,
    requestStatus: getRequestStatus(row),
    approvedByName: row.ApprovedDirect ? 'Líder directo' : '',
  };
  if (isVacaciones(row)) {
    // MOCK — backend debe enviar saldoVacaciones y diasDisponibles
    return { ...base, saldoVacaciones: 12, diasDisponibles: 8 };
  }
  return base;
}

// DBSB-415: una incidencia está escalada a CH cuando el backend la marcó explícitamente
export function isEscalatedToCH(row: IncidentStatusFlags): boolean {
  return getRequestStatus(row) === REQUEST_STATUS.ESCALADA || Boolean(row.escalatedToCH);
}

// Regla de negocio: CH puede actuar sobre una incidencia cuando:
//   · requestStatus='Escalada' — el cron la escaló a CH (cubre todos los conceptos
//     del cron: idConcept IN (10,52,53,54), no solo vacaciones). Estado canónico:
//     si el backend lo marcó 'Escalada', CH tiene prioridad de decisión (HU-VAC-05).
//   · ApprovedDirect=1, ApprovedRH=0 — el líder aprobó y CH aún no ha actuado (flujo normal).
export function isSelectableForApproval(row: IncidentStatusFlags): boolean {
  if (getRequestStatus(row) === REQUEST_STATUS.ESCALADA) return true;
  return Boolean(row.ApprovedDirect) && !row.ApprovedRH && getRequestStatus(row) !== REQUEST_STATUS.RECHAZADA;
}

// RN-17 (DBSB-222): alerta homologada de "período cerrado" para registro, aprobación y cancelación.
// PA-05: un período está cerrado cuando la nómina de ese período ya fue generada (backend lo determina).
export const PERIODO_CERRADO_MSG =
  "Período cerrado: no es posible realizar la operación porque la nómina del período ya fue generada.";

// Detecta si el error del backend corresponde a un período cerrado (RN-17 / DBSB-222).
// Prioriza extensions.code === 'PERIODO_CERRADO' (contrato formal con Apollo);
// cae a heurística de palabras clave como fallback de compatibilidad durante la transición.
export function esPeriodoCerrado(error: any): boolean {
  if (error?.graphQLErrors?.[0]?.extensions?.code === 'PERIODO_CERRADO') return true;
  const m = ((typeof error === 'string' ? error : error?.message) || "").toLowerCase();
  return (
    m.includes("cerrad") ||
    m.includes("nómina") ||
    m.includes("nomina") ||
    m.includes("ya pasó") ||
    m.includes("ya paso") ||
    m.includes("vencid")
  );
}

// RN-SP-03/04: SEND_DISPERSION rechaza la dispersión si se coló un colaborador de
// Servicios Profesionales, con extensions.code = 'BUSINESS_RULE_VIOLATION' y el
// message listando los nombres afectados — se muestra tal cual, es seguro para el usuario.
export function esReglaNegocioViolada(error: any): boolean {
  return error?.graphQLErrors?.[0]?.extensions?.code === 'BUSINESS_RULE_VIOLATION';
}

export function getGraphQLErrorMessage(error: any, fallback: string): string {
  return error?.graphQLErrors?.[0]?.message || error?.message || fallback;
}

// DBSB-464 — badge de estatus para el listado completo de incidencias en Pre-Nómina.
// Normaliza los distintos valores que puede traer requestStatus del backend
// (Pendiente/Enviada, Aprobada/AprobadaLider, etc.) y deriva desde los flags
// ApprovedDirect/ApprovedRH cuando requestStatus sea NULL (entornos con datos viejos).
export interface PrenominaStatusView { label: string; color: string; }

const PRENOMINA_STATUS_VIEW: Record<string, PrenominaStatusView> = {
  Enviada:       { label: 'Pendiente',       color: '#f5b800' },
  Pendiente:     { label: 'Pendiente',       color: '#f5b800' },
  AprobadaLider: { label: 'Aprobada líder',  color: '#5bab6e' },
  Aprobada:      { label: 'Aprobada líder',  color: '#5bab6e' },
  Autorizada:    { label: 'Autorizada',      color: '#2d9b6f' },
  Escalada:      { label: 'Escalada a CH',   color: '#e8832a' },
  Rechazada:     { label: 'Rechazada',       color: '#e02020' },
  Cancelada:     { label: 'Cancelada',       color: '#8a99ad' },
};

export function normalizePrenominaStatus(row: {
  requestStatus?: string | null;
  ApprovedDirect?: number | boolean | null;
  ApprovedRH?: number | boolean | null;
}): PrenominaStatusView {
  const raw = (row.requestStatus && PRENOMINA_STATUS_VIEW[row.requestStatus])
    ? row.requestStatus
    : getRequestStatus({
        ApprovedDirect: row.ApprovedDirect ?? 0,
        ApprovedRH:     row.ApprovedRH     ?? 0,
      });
  return PRENOMINA_STATUS_VIEW[raw] ?? { label: raw || '—', color: '#8a99ad' };
}
